import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Locale } from "../../constants/locale";
import useEmployerSite from "../../contexts/EmployerSiteContext";

const INTERVAL_TIME_IN_MS = 200;
const MAX_INTERVAL_TIME_IN_MS = 3000;

enum AinoLanguage {
  Finnish = "fi-FI",
  English = "en-US",
}

enum AinoFilterValue {
  Employees = "employees",
  Employers = "employers",
}

export function useAinoChatPanel() {
  const { isEmployerSite } = useEmployerSite();
  const router = useRouter();
  const [chatPanel, setChatPanel] = useState<BoostChatPanel | null>(null);
  const timeSpentRef = useRef(0);

  const language =
    router.locale === Locale.Finnish
      ? AinoLanguage.Finnish
      : AinoLanguage.English;

  const options: AinoBoostChatPanelOptions = useMemo(() => {
    const filterValues: AinoFilterValue[] = [
      isEmployerSite ? AinoFilterValue.Employers : AinoFilterValue.Employees,
    ];

    return {
      apiUrlBase: "https://122finlandworks.boost.ai/api",
      language,
      filterValues,
    };
  }, [language, isEmployerSite]);

  const initPanel = useCallback(
    (currentIntervalId: number) => {
      const ainoChatPanel = window.boostChatPanel?.(options);

      if (ainoChatPanel) {
        window.clearInterval(currentIntervalId);
        setChatPanel(ainoChatPanel);
      }

      timeSpentRef.current += INTERVAL_TIME_IN_MS;
      if (timeSpentRef.current > MAX_INTERVAL_TIME_IN_MS) {
        window.clearInterval(currentIntervalId);
      }
    },
    [options]
  );

  useEffect(() => {
    const intervalId = window.setInterval(
      () => initPanel(intervalId),
      INTERVAL_TIME_IN_MS
    );

    initPanel(intervalId);

    return () => window.clearInterval(intervalId);
  }, [initPanel]);

  return chatPanel;
}
